/* Cart drawer: <cart-drawer> custom element, cart rendering, quantity/discount,
   free-shipping bar, and the add-on tray (assets/cart-addons.js). Loaded by snippets/cart-drawer.liquid.

   Speed + conversion pass (2026-10-01):
   - The drawer opens at once and fills in when the cart arrives (what it showed last time, or a skeleton, is on screen
     meanwhile) instead of waiting for cart.js; add-to-cart callers can announce the add with `cart-drawer:adding` and
     report a failure with `cart-drawer:add-failed`.
   - Quantity buttons are optimistic and queued, so rapid clicks all count (before, three quick + clicks added one).
   - Remove has an Undo; line and cart discounts are shown; the Checkout button shows it is working.
   - Item pictures are requested at display size (cart.js returns the original upload, up to 2000 px). */
(function () {
  const AMOUNT_PLACEHOLDER = '\x7b\x7b amount \x7d\x7d';
  const AMOUNT_RE = /\x7b\x7b\s*amount\s*\x7d\x7d/g;
  const COUNT_PLACEHOLDER = '\x7b\x7b count \x7d\x7d';
  const TITLE_PLACEHOLDER = '\x7b\x7b title \x7d\x7d';
  const CART_CACHE_KEY = 'kb-cart-cache';
  const CART_CACHE_TTL = 30 * 60 * 1000;
  const READINESS_KEY = 'readiness-html-cache';
  const READINESS_TTL = 30 * 60 * 1000;

  // Locale/market-aware root ("/" on the primary domain, e.g. "/en-ca/" under a subfolder).
  function cdCartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function cdStorageGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function cdStorageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function cdEscapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // Shopify serves any size of an image from its CDN: ask for the size we draw instead of the original upload.
  function cdImage(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.href);
      if (!/(^|\.)cdn\.shopify\.com$/.test(u.hostname) && u.pathname.indexOf('/cdn/shop/') === -1) return url;
      u.searchParams.set('width', width);
      return u.href;
    } catch (e) {
      return url;
    }
  }

  // Keeps only bold/italic tags (no attributes) from merchant rich text; other
  // tags are unwrapped to their text, and script/style content is dropped.
  var CD_INLINE_TAGS = { STRONG: 1, B: 1, EM: 1, I: 1 };
  var CD_DROP_TAGS = { SCRIPT: 1, STYLE: 1, TEMPLATE: 1, IFRAME: 1, OBJECT: 1, EMBED: 1 };
  function cdSanitizeInlineHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html == null ? '' : html);
    (function clean(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 1) {
          var tag = child.tagName.toUpperCase(); // SVG/MathML tags are lowercase
          if (CD_DROP_TAGS[tag] || tag === 'SVG' || tag === 'MATH') { child.remove(); return; }
          clean(child);
          if (CD_INLINE_TAGS[tag]) {
            while (child.attributes.length) child.removeAttribute(child.attributes[0].name);
          } else {
            child.replaceWith.apply(child, Array.prototype.slice.call(child.childNodes));
          }
        } else if (child.nodeType !== 3) {
          child.remove();
        }
      });
    })(tpl.content);
    var box = document.createElement('div');
    box.appendChild(tpl.content);
    return box.innerHTML.trim();
  }

  function cdNormalizeText(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.emptyText = this.dataset.emptyText || 'Your cart is empty';
      this.removeText = this.dataset.removeText || 'Remove';
      this.decreaseQuantityText = this.dataset.decreaseQuantityText || 'Decrease quantity';
      this.increaseQuantityText = this.dataset.increaseQuantityText || 'Increase quantity';
      this.moneyFormat = this.dataset.moneyFormat || '$' + AMOUNT_PLACEHOLDER;
      this.shippingQualified = this.dataset.shippingQualified || "You've qualified for free shipping!";
      this.shippingThreshold = this.dataset.shippingThreshold || "You're " + AMOUNT_PLACEHOLDER + ' away from free shipping.';
      this.shippingThresholdCents = (parseInt(this.dataset.shippingAmount, 10) || 150) * 100;
      this.isB2B = this.dataset.b2b === 'true';
      this.helmetThreshold = parseInt(this.dataset.helmetThreshold, 10) || 6;
      this.shippingThresholdB2B = this.dataset.shippingThresholdB2b || 'Add ' + COUNT_PLACEHOLDER + ' more helmet(s) for free shipping.';
      this.discountApplied = this.dataset.discountApplied || 'Discount applied';
      this.discountApplying = this.dataset.discountApplying || 'Applying…';
      this.discountInvalid = this.dataset.discountInvalid || 'Invalid discount code';
      this.discountNotApplicable = this.dataset.discountNotApplicable || 'This code can’t be used with your current cart';
      this.removedText = this.dataset.removedText || TITLE_PLACEHOLDER + ' removed';
      this.undoText = this.dataset.undoText || 'Undo';
      this.refreshFailedText = this.dataset.refreshFailed || 'Couldn’t update your cart. Please try again.';
      this.panel = this.querySelector('.cart-drawer__panel');
      this.itemsContainer = this.querySelector('[data-cart-items]');
      this.footer = this.querySelector('[data-cart-footer]');
      this.cartCount = this.querySelector('[data-cart-count]');
      this.cartSubtotal = this.querySelector('[data-cart-subtotal]');
      this.savingsRow = this.querySelector('[data-cart-savings]');
      this.savingsAmount = this.querySelector('[data-cart-savings-amount]');
      this.notice = this.querySelector('[data-cart-notice]');
      this.shipping = this.querySelector('[data-cart-shipping]');
      this.shippingBar = this.querySelector('[data-shipping-bar]');
      this.shippingProgress = this.querySelector('[data-shipping-progress]');
      this.shippingMessage = this.querySelector('[data-shipping-message]');
      this.discountInput = this.querySelector('[data-discount-input]');
      this.discountApply = this.querySelector('[data-discount-apply]');
      this.discountMessage = this.querySelector('[data-discount-message]');
      this.checkoutForm = this.querySelector('[data-cart-checkout-form]');
      // The code field lives in a closed <details>; open it whenever a result message appears.
      const codeDetails = this.querySelector('[data-discount-details]');
      if (codeDetails && this.discountMessage) {
        new MutationObserver(() => { if (!this.discountMessage.hidden) codeDetails.open = true; })
          .observe(this.discountMessage, { attributes: true, attributeFilter: ['hidden'] });
      }
      this.closeButtons = this.querySelectorAll('[data-cart-drawer-close]');

      this.lastCart = null; // the cart as last drawn
      this.seq = 0; // newest refresh wins
      this.targets = {}; // line key -> quantity the shopper asked for, not yet sent
      this.flushTimer = null;
      this.flushing = null;
      this.removed = null; // the line just removed, for Undo
      this.noticeTimer = null;
      this.autoOpened = false; // opened by an add-to-cart that is still in flight
      this.itemsHtml = null;
      this.readinessCache = {};
      try { Object.assign(this.readinessCache, JSON.parse(cdStorageGet(READINESS_KEY) || '{}')); } catch (e) {}

      // The add-on tray ("Complete your setup", assets/cart-addons.js): rebuilt from the cart after every change.
      var addonsEl = this.querySelector('[data-addons="drawer"]');
      this.addons = addonsEl && window.KabutoAddons
        ? window.KabutoAddons.mount(addonsEl, { mode: 'drawer', onAdded: () => this.refresh() })
        : null;
    }

    connectedCallback() {
      this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));

      this.panel.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });

      this.panel.addEventListener('click', (e) => {
        if (e.target === this.panel) this.close();
      });

      this.discountApply.addEventListener('click', () => this.applyDiscount());

      document.addEventListener('cart-drawer:open', () => this.open());
      // An add-to-cart is on its way: open now (skeleton or last cart, dimmed) instead of after the round trips.
      document.addEventListener('cart-drawer:adding', () => this.show(true));
      document.addEventListener('cart-drawer:add-failed', () => {
        if (this.autoOpened) this.close();
        this.autoOpened = false;
      });
      // Warm the wallets up (76 KB of Shopify script) only when someone reaches for the cart: pointing at, touching or focusing the cart
      // button, the sticky bar or an add-to-cart control. There is no timer and no scroll/mouse-move trigger on purpose: those fire for every
      // visitor and for PageSpeed's own load window, and the wallets were landing in every page's first 10 seconds. Opening the drawer
      // (show) mounts them anyway; mountWallets does nothing once mounted.
      const warmSelector = '[data-cart-drawer-open], .header__cart, .cs__sticky-cart, [data-addons-add], [data-pinlock-add], form[action*="/cart/add"]';
      const warm = (e) => {
        if (e.target.closest && e.target.closest(warmSelector)) this.mountWallets();
      };
      ['pointerover', 'touchstart', 'focusin'].forEach((evt) => document.addEventListener(evt, warm, { passive: true }));

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-open]');
        if (trigger) {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        }
      });

      if (this.itemsContainer) {
        this.itemsContainer.addEventListener('click', (e) => {
          const target = e.target.closest('[data-qty-down], [data-qty-up], [data-item-remove]');
          if (!target) return;
          if (target.hasAttribute('data-qty-down')) {
            this.changeItem(target.dataset.qtyDown, -1);
          } else if (target.hasAttribute('data-qty-up')) {
            this.changeItem(target.dataset.qtyUp, 1);
          } else if (target.hasAttribute('data-item-remove')) {
            this.changeItem(target.dataset.itemRemove, 0);
          }
        });
      } else {
        console.warn('CartDrawer: itemsContainer is null — quantity/remove buttons will not work');
      }

      if (this.notice) {
        this.notice.addEventListener('click', (e) => {
          if (e.target.closest('[data-cart-undo]')) this.undoRemove();
        });
      }

      // Checkout: wait for any quantity change still on its way, then show that something is happening (the checkout page takes a moment).
      if (this.checkoutForm) {
        const btn = this.checkoutForm.querySelector('.cart-drawer__checkout');
        this.checkoutForm.addEventListener('submit', (e) => {
          if (this.flushing || Object.keys(this.targets).length) {
            e.preventDefault();
            this.flushAll().then(() => this.checkoutForm.requestSubmit(btn));
            return;
          }
          if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
        });
        // Back from checkout (bfcache) must not leave the button spinning.
        window.addEventListener('pageshow', () => {
          if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
        });
      }
    }

    formatMoney(cents) {
      try {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        var value = (Math.round(cents) / 100).toFixed(2);
        return this.moneyFormat.replace(AMOUNT_RE, value);
      } catch (e) {
        return '$' + (Math.round(cents) / 100).toFixed(2);
      }
    }

    // Mount the accelerated-checkout wallets (kept in an inert <template> until now) the first time the drawer is used.
    mountWallets() {
      const host = this.querySelector('[data-cart-accelerated]');
      const tpl = host && host.querySelector('template[data-cart-accelerated-template]');
      if (!tpl) return;
      host.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
    }

    // ---- open / close ----------------------------------------------------------------------------------------------

    // The header badge is rendered by the server (and kept in step by this drawer), so it says how many items the cart really has.
    knownCount() {
      const badge = document.querySelector('.header__cart-count');
      if (!badge) return null;
      return badge.hidden ? 0 : parseInt(badge.textContent, 10) || 0;
    }

    cachedCart() {
      try {
        const hit = JSON.parse(cdStorageGet(CART_CACHE_KEY) || 'null');
        if (!hit || !hit.cart || Date.now() - hit.at > CART_CACHE_TTL) return null;
        // Only trust it when it agrees with the badge: a cart changed in another tab or after a purchase must not flash old items.
        return hit.cart.item_count > 0 && hit.cart.item_count === this.knownCount() ? hit.cart : null;
      } catch (e) {
        return null;
      }
    }

    saveCart(cart) {
      this.lastCart = cart;
      cdStorageSet(CART_CACHE_KEY, JSON.stringify({ at: Date.now(), cart: cart }));
    }

    skeletonMarkup() {
      return '<div class="cart-drawer__skeleton" aria-hidden="true"><div class="cart-drawer__skeleton-img"></div>' +
        '<div class="cart-drawer__skeleton-lines"><span></span><span></span><span></span></div></div>';
    }

    // Slide the panel in straight away. What it shows meanwhile: the cart as last drawn, the stored copy (when it matches the badge), else a skeleton.
    show(adding) {
      document.querySelectorAll('link[data-cart-drawer-css]').forEach((l) => { l.media = 'all'; });
      this.mountWallets();
      if (this.panel.open) return;
      this.autoOpened = !!adding;
      if (!this.lastCart) {
        const cached = this.cachedCart();
        if (cached) this.render(cached);
      }
      if (!this.lastCart || !this.lastCart.item_count || adding && this.knownCount() === 0) {
        this.itemsContainer.innerHTML = this.skeletonMarkup();
        this.itemsHtml = null;
      }
      this.panel.setAttribute('aria-busy', 'true');
      this.panel.showModal();
      requestAnimationFrame(() => {
        this.panel.classList.add('cart-drawer__panel--open');
        var closeBtn = this.closeButtons[0];
        if (closeBtn) closeBtn.focus();
      });
      document.body.style.overflow = 'hidden';
    }

    async open() {
      this.autoOpened = false;
      this.show(false);
      const cart = await this.refresh();
      document.dispatchEvent(new CustomEvent('cart-drawer:opened', { detail: { cart: cart } }));
    }

    close() {
      if (!this.panel.open) return;
      this.panel.classList.remove('cart-drawer__panel--open');
      document.body.style.overflow = '';
      this.hideNotice();
      setTimeout(() => { this.panel.close(); }, 300);
    }

    // ---- loading the cart ------------------------------------------------------------------------------------------

    // Fetch the cart and draw it. Only the newest call draws, so a slow older answer can't overwrite a newer one.
    async refresh() {
      const seq = ++this.seq;
      this.panel.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(cdCartUrl('cart.js'));
        const cart = await response.json();
        if (seq === this.seq) this.render(cart);
        return cart;
      } catch (err) {
        console.error('CartDrawer fetchCart error:', err);
        if (seq === this.seq) {
          if (this.lastCart && this.lastCart.item_count) this.showNotice(this.refreshFailedText, 6000);
          else this.itemsContainer.innerHTML = this.emptyMarkup();
        }
        return null;
      } finally {
        if (seq === this.seq) this.panel.removeAttribute('aria-busy');
      }
    }

    fetchCart() { return this.refresh(); }

    emptyMarkup() {
      const url = this.dataset.emptyUrl;
      const cta = this.dataset.emptyCta;
      return `
        <div class="cart-drawer__empty">
          <p>${this.emptyText}</p>
          ${url && cta ? `<a class="btn" href="${cdEscapeHtml(url)}">${cdEscapeHtml(cta)}</a>` : ''}
        </div>`;
    }

    setItems(html) {
      if (html === this.itemsHtml) return; // nothing changed: leave the DOM (and focus, and the images) alone
      const active = document.activeElement;
      let refocus = null;
      if (active && this.itemsContainer.contains(active)) {
        ['data-qty-up', 'data-qty-down', 'data-item-remove'].forEach((a) => { if (active.hasAttribute(a)) refocus = '[' + a + '="' + active.getAttribute(a) + '"]'; });
      }
      this.itemsHtml = html;
      this.itemsContainer.innerHTML = html;
      if (refocus) { const again = this.itemsContainer.querySelector(refocus); if (again) again.focus(); }
    }

    render(cart) {
      this.saveCart(cart);
      this.cartCount.textContent = cart.item_count;
      this.updateHeaderCount(cart.item_count);

      if (this.addons) this.addons.update(cart);
      if (cart.item_count === 0) {
        this.setItems(this.emptyMarkup());
        this.footer.hidden = true;
        this.shipping.hidden = true;
        return;
      }

      this.footer.hidden = false;
      this.renderItems(cart.items);
      this.formatReadinessNotes();
      this.renderTotals(cart);
    }

    renderTotals(cart) {
      this.renderSubtotal(cart);
      this.renderSavings(cart);
      this.renderShipping(cart);
      this.renderDiscount(cart);
    }

    // ---- readiness (bold/italic version of the plain text saved on the cart line) -----------------------------------

    // The product page already holds the formatted readiness: store it for this product so the drawer needs no request for it.
    primeReadinessFromPage() {
      const el = document.querySelector('.ps__readiness-text');
      const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (!el || !m) return;
      const handle = decodeURIComponent(m[1]);
      const hit = this.readinessCache[handle];
      if (hit && Date.now() - hit.at < READINESS_TTL) return;
      this.readinessCache[handle] = { html: cdSanitizeInlineHtml(el.innerHTML), at: Date.now() };
    }

    // Formatted readiness for a cart line when we already have it and it says the same as the saved note.
    readinessHtml(item) {
      const note = item.properties && item.properties._readiness;
      if (!note) return '';
      const hit = this.readinessCache[item.handle];
      if (hit && hit.html && Date.now() - hit.at < READINESS_TTL) {
        const probe = document.createElement('span');
        probe.innerHTML = hit.html;
        if (cdNormalizeText(probe.textContent) === cdNormalizeText(note)) return hit.html;
      }
      return cdEscapeHtml(note);
    }

    // The cart line stores readiness as plain text (it's saved on the order).
    // For display, swap in the product's formatted version (bold/italic) from
    // the product-readiness section, fetched once per product per visit (and not at all on that product's own page).
    // It's only used when its text matches the note, so the drawer never shows
    // something different from what was added.
    async formatReadinessNotes() {
      var notes = Array.prototype.slice.call(this.itemsContainer.querySelectorAll('[data-readiness-handle]'));
      if (!notes.length) return;
      var self = this;
      var cache = this.readinessCache;
      var handles = notes.map(function (n) { return n.dataset.readinessHandle; })
        .filter(function (h, i, all) { return h && all.indexOf(h) === i; });
      var missing = handles.filter(function (h) { var hit = cache[h]; return !(hit && Date.now() - hit.at < READINESS_TTL); });
      if (!missing.length) return;

      await Promise.all(missing.map(async function (handle) {
        try {
          var res = await fetch(cdCartUrl('products/' + encodeURIComponent(handle) + '?sections=product-readiness'));
          if (!res.ok) return;
          var data = await res.json();
          var doc = new DOMParser().parseFromString(data['product-readiness'] || '', 'text/html');
          var tpl = doc.querySelector('template[data-readiness-html]');
          cache[handle] = { html: tpl ? cdSanitizeInlineHtml(tpl.innerHTML) : '', at: Date.now() };
        } catch (e) {}
      }));

      cdStorageSet(READINESS_KEY, JSON.stringify(cache));

      Array.prototype.slice.call(this.itemsContainer.querySelectorAll('[data-readiness-handle]')).forEach(function (note) {
        var hit = cache[note.dataset.readinessHandle];
        if (!hit || !hit.html) return;
        var probe = document.createElement('span');
        probe.innerHTML = hit.html;
        if (cdNormalizeText(probe.textContent) === cdNormalizeText(note.textContent)) {
          note.innerHTML = hit.html;
          self.itemsHtml = null; // the DOM no longer equals the string we drew
        }
      });
    }

    // ---- items -----------------------------------------------------------------------------------------------------

    // Line price, with the price before discounts struck through and the discount names under it when a discount applies.
    linePriceMarkup(item) {
      const saved = item.original_line_price > item.final_line_price;
      let html = '';
      if (saved) html += `<s class="cart-drawer__item-was">${this.formatMoney(item.original_line_price)}</s>`;
      html += `<span class="cart-drawer__item-line-price">${this.formatMoney(item.final_line_price)}</span>`;
      return html;
    }

    discountNotes(item) {
      const names = (item.line_level_discount_allocations || [])
        .map((a) => a.discount_application && a.discount_application.title)
        .filter((t, i, all) => t && all.indexOf(t) === i);
      return names.length ? `<span class="cart-drawer__item-discount">${names.map(cdEscapeHtml).join(', ')}</span>` : '';
    }

    itemMarkup(item) {
      const img = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      const alt = cdEscapeHtml(item.product_title);
      const imageHtml = img
        ? `<img src="${cdEscapeHtml(cdImage(img, 160))}" srcset="${cdEscapeHtml(cdImage(img, 160))} 160w, ${cdEscapeHtml(cdImage(img, 240))} 240w, ${cdEscapeHtml(cdImage(img, 320))} 320w" sizes="92px" alt="${alt}" loading="lazy" decoding="async" width="80" height="80">`
        : '';
      return `
        <div class="cart-drawer__item" data-item-id="${item.key}" data-qty="${item.quantity}">
          <div class="cart-drawer__item-image">${imageHtml}</div>
          <div class="cart-drawer__item-details">
            <div class="cart-drawer__item-header">
              <p class="cart-drawer__item-title">
                <a href="${cdEscapeHtml(item.url)}">${cdEscapeHtml(item.product_title)}</a>
              </p>
              <span class="cart-drawer__item-price">${this.formatMoney(item.original_price)}</span>
            </div>
            ${item.variant_title ? `<span class="cart-drawer__item-variant">${cdEscapeHtml(item.variant_title)}</span>` : ''}
            ${item.selling_plan_allocation ? `<span class="cart-drawer__item-variant">${cdEscapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>` : ''}
            ${item.properties && item.properties._readiness ? `<span class="cart-drawer__item-readiness" data-readiness-handle="${cdEscapeHtml(item.handle)}">${this.readinessHtml(item)}</span>` : ''}
            <span data-item-discounts>${this.discountNotes(item)}</span>
            <button class="cart-drawer__item-remove" data-item-remove="${item.key}">${this.removeText}</button>
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__item-quantity">
                <button type="button" class="cart-drawer__item-qty-btn" data-qty-down="${item.key}" aria-label="${this.decreaseQuantityText}"${item.quantity <= 1 ? ' disabled' : ''}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
                <span class="cart-drawer__item-qty-value">${item.quantity}</span>
                <button type="button" class="cart-drawer__item-qty-btn" data-qty-up="${item.key}" aria-label="${this.increaseQuantityText}">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </div>
              <span class="cart-drawer__item-line" data-item-line>${this.linePriceMarkup(item)}</span>
            </div>
          </div>
        </div>
      `;
    }

    renderItems(items) {
      this.primeReadinessFromPage();
      this.setItems(items.map((item) => this.itemMarkup(item)).join(''));
    }

    // After a quantity change the lines are patched in place (no flicker, focus stays on the button) when the same lines are still in the cart.
    patchItems(cart) {
      const els = Array.prototype.slice.call(this.itemsContainer.querySelectorAll('[data-item-id]'));
      if (els.length !== cart.items.length) return false;
      for (let i = 0; i < els.length; i++) if (els[i].dataset.itemId !== cart.items[i].key) return false;
      cart.items.forEach((item, i) => {
        const el = els[i];
        el.dataset.qty = item.quantity;
        el.querySelector('.cart-drawer__item-qty-value').textContent = item.quantity;
        const down = el.querySelector('[data-qty-down]');
        if (down) down.disabled = item.quantity <= 1;
        const line = el.querySelector('[data-item-line]');
        if (line) line.innerHTML = this.linePriceMarkup(item);
        const notes = el.querySelector('[data-item-discounts]');
        if (notes) notes.innerHTML = this.discountNotes(item);
      });
      this.itemsHtml = null;
      return true;
    }

    // ---- quantity: optimistic and queued ---------------------------------------------------------------------------

    changeItem(lineId, delta) {
      const itemEl = this.itemsContainer.querySelector(`[data-item-id="${lineId}"]`);
      if (!itemEl) return;

      const current = lineId in this.targets ? this.targets[lineId] : parseInt(itemEl.dataset.qty, 10);
      const next = delta === 0 ? 0 : Math.max(0, current + delta);
      if (next === current) return;
      this.targets[lineId] = next;
      this.itemsHtml = null;

      if (next === 0) {
        const line = this.lastCart && this.lastCart.items.find((i) => String(i.key) === String(lineId));
        if (line) this.removed = line;
        itemEl.remove();
        if (line) this.showNotice(this.removedText.replace(TITLE_PLACEHOLDER, line.product_title), 8000, true);
      } else {
        itemEl.dataset.qty = next;
        itemEl.querySelector('.cart-drawer__item-qty-value').textContent = next;
        const down = itemEl.querySelector('[data-qty-down]');
        if (down) down.disabled = next <= 1;
        const line = this.lastCart && this.lastCart.items.find((i) => String(i.key) === String(lineId));
        const lineEl = itemEl.querySelector('[data-item-line]');
        // Unit price times quantity when no discount is involved; otherwise the old figure stays until the server answers.
        if (line && lineEl && line.original_line_price === line.final_line_price) {
          lineEl.innerHTML = `<span class="cart-drawer__item-line-price">${this.formatMoney(line.original_price * next)}</span>`;
        }
      }
      // Counts follow the click too (header badge, drawer title), and an emptied cart shows its empty state right away.
      if (this.lastCart) {
        const count = this.lastCart.items.reduce((n, i) => n + (i.key in this.targets ? this.targets[i.key] : i.quantity), 0);
        this.cartCount.textContent = count;
        this.updateHeaderCount(count);
        if (count === 0) {
          this.itemsContainer.innerHTML = this.emptyMarkup();
          this.footer.hidden = true;
          this.shipping.hidden = true;
        }
      }
      this.footer.dataset.syncing = 'true';
      clearTimeout(this.flushTimer);
      this.flushTimer = setTimeout(() => this.flush(), 220);
    }

    // Send the queued quantities one after another; a click that arrives meanwhile is picked up by the same loop.
    flush() {
      if (this.flushing) return this.flushing;
      this.flushing = (async () => {
        let cart = null;
        try {
          while (Object.keys(this.targets).length) {
            const key = Object.keys(this.targets)[0];
            const qty = this.targets[key];
            const response = await fetch(cdCartUrl('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: key, quantity: qty })
            });
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              const err = new Error(body.description || body.message || response.statusText);
              err.fromServer = true;
              throw err;
            }
            cart = await response.json();
            if (this.targets[key] === qty) delete this.targets[key];
          }
        } catch (err) {
          console.error('CartDrawer changeItem error:', err);
          this.targets = {};
          this.showNotice(err.fromServer && err.message ? err.message : this.refreshFailedText, 6000);
          cart = null;
          this.itemsHtml = null;
        } finally {
          delete this.footer.dataset.syncing;
        }
        if (cart) this.applyCart(cart);
        else await this.refresh();
      })().finally(() => {
        this.flushing = null;
        if (Object.keys(this.targets).length) this.flush();
      });
      return this.flushing;
    }

    async flushAll() {
      clearTimeout(this.flushTimer);
      while (this.flushing || Object.keys(this.targets).length) await (this.flushing || this.flush());
    }

    // Draw the cart that came back from a quantity change.
    applyCart(cart) {
      this.saveCart(cart);
      this.cartCount.textContent = cart.item_count;
      this.updateHeaderCount(cart.item_count);
      if (cart.item_count === 0) {
        this.render(cart);
        return;
      }
      this.footer.hidden = false;
      if (!this.patchItems(cart)) this.renderItems(cart.items);
      this.renderTotals(cart);
      if (this.addons) this.addons.update(cart);
    }

    // ---- notices, Undo ---------------------------------------------------------------------------------------------

    showNotice(text, ms, withUndo) {
      if (!this.notice) return;
      this.notice.textContent = '';
      const span = document.createElement('span');
      span.textContent = text;
      this.notice.appendChild(span);
      if (withUndo) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-drawer__notice-undo';
        btn.setAttribute('data-cart-undo', '');
        btn.textContent = this.undoText;
        this.notice.appendChild(btn);
      }
      this.notice.hidden = false;
      clearTimeout(this.noticeTimer);
      if (ms) this.noticeTimer = setTimeout(() => this.hideNotice(), ms);
    }

    hideNotice() {
      if (!this.notice) return;
      clearTimeout(this.noticeTimer);
      this.notice.hidden = true;
      this.notice.textContent = '';
    }

    // Put the line that was just removed back (same variant, quantity and properties).
    async undoRemove() {
      const item = this.removed;
      if (!item) return;
      this.removed = null;
      this.hideNotice();
      this.panel.setAttribute('aria-busy', 'true');
      const body = { id: item.variant_id, quantity: item.quantity };
      const props = {};
      Object.keys(item.properties || {}).forEach((k) => { if (item.properties[k] != null && item.properties[k] !== '') props[k] = item.properties[k]; });
      if (Object.keys(props).length) body.properties = props;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) body.selling_plan = item.selling_plan_allocation.selling_plan.id;
      try {
        const response = await fetch(cdCartUrl('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error();
      } catch (err) {
        this.showNotice(this.refreshFailedText, 6000);
      }
      await this.refresh();
    }

    // ---- totals ----------------------------------------------------------------------------------------------------

    renderSubtotal(cart) {
      this.cartSubtotal.textContent = this.formatMoney(cart.total_price);
    }

    // What the shopper saves (line and cart discounts together), shown above the subtotal.
    renderSavings(cart) {
      if (!this.savingsRow) return;
      const saved = cart.total_discount || 0;
      this.savingsRow.hidden = saved <= 0;
      if (saved > 0 && this.savingsAmount) this.savingsAmount.textContent = '−' + this.formatMoney(saved);
    }

    renderShipping(cart) {
      const currentLabel = this.querySelector('[data-shipping-current]');

      if (this.isB2B) {
        const helmetCount = cart.items.reduce(function (sum, item) {
          const isHelmet = item.properties && item.properties._is_helmet;
          return isHelmet ? sum + item.quantity : sum;
        }, 0);

        if (currentLabel) currentLabel.textContent = String(helmetCount);

        const remainingHelmets = this.helmetThreshold - helmetCount;
        if (remainingHelmets <= 0) {
          this.shipping.hidden = false;
          this.shippingProgress.style.width = '100%';
          this.shippingMessage.textContent = this.shippingQualified;
          this.shipping.classList.add('cart-drawer__shipping--qualified');
          if (this.shippingBar) this.shippingBar.setAttribute('aria-valuenow', '100');
        } else {
          this.shipping.hidden = false;
          const pct = Math.min((helmetCount / this.helmetThreshold) * 100, 100);
          this.shippingProgress.style.width = `${pct}%`;
          this.shippingMessage.textContent =
            this.shippingThresholdB2B.replace(COUNT_PLACEHOLDER, String(remainingHelmets));
          this.shipping.classList.remove('cart-drawer__shipping--qualified');
          if (this.shippingBar) this.shippingBar.setAttribute('aria-valuenow', String(Math.round(pct)));
        }
        return;
      }

      if (currentLabel) currentLabel.textContent = this.formatMoney(cart.total_price);

      const remaining = this.shippingThresholdCents - cart.total_price;
      let pct;
      if (remaining <= 0) {
        this.shipping.hidden = false;
        pct = 100;
        this.shippingProgress.style.width = '100%';
        this.shippingMessage.textContent = this.shippingQualified;
        this.shipping.classList.add('cart-drawer__shipping--qualified');
      } else {
        this.shipping.hidden = false;
        pct = Math.min((cart.total_price / this.shippingThresholdCents) * 100, 100);
        this.shippingProgress.style.width = `${pct}%`;
        this.shippingMessage.textContent =
          this.shippingThreshold.replace(AMOUNT_PLACEHOLDER, this.formatMoney(remaining));
        this.shipping.classList.remove('cart-drawer__shipping--qualified');
      }
      if (this.shippingBar) this.shippingBar.setAttribute('aria-valuenow', String(Math.round(pct)));
    }

    // Shopify keeps a code on the cart even when it doesn't apply (applicable: false),
    // so only an applicable one counts as applied.
    findDiscountCode(cart, code) {
      const codes = (cart && cart.discount_codes) || [];
      if (!code) return codes.find((d) => d.applicable) || null;
      return codes.find((d) => d.code.toLowerCase() === code.toLowerCase()) || null;
    }

    renderDiscount(cart) {
      const applied = this.findDiscountCode(cart);
      if (applied) {
        this.discountInput.value = applied.code;
        this.discountMessage.hidden = false;
        this.discountMessage.textContent = this.discountApplied;
        this.discountMessage.style.color = '#2e7d32';
      }
    }

    async applyDiscount() {
      const code = this.discountInput.value.trim();
      if (!code) return;

      this.discountMessage.hidden = false;
      this.discountMessage.textContent = this.discountApplying;

      try {
        const response = await fetch(cdCartUrl('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount_code: code })
        });
        if (!response.ok) throw new Error();
        // update.js answers 200 even for unknown codes; the returned cart says
        // whether the code was actually accepted.
        const match = this.findDiscountCode(await response.json(), code);
        await this.refresh();
        this.discountMessage.hidden = false;
        if (match && match.applicable) {
          this.discountMessage.textContent = this.discountApplied;
          this.discountMessage.style.color = '#2e7d32';
        } else {
          this.discountMessage.textContent = match ? this.discountNotApplicable : this.discountInvalid;
          this.discountMessage.style.color = '#d62828';
        }
      } catch (err) {
        console.error('CartDrawer applyDiscount error:', err);
        this.discountMessage.hidden = false;
        this.discountMessage.textContent = this.discountInvalid;
        this.discountMessage.style.color = '#d62828';
      }
    }

    updateHeaderCount(count) {
      const headerBadge = document.querySelector('.header__cart-count');
      if (headerBadge) {
        if (count > 0) {
          headerBadge.textContent = count;
          headerBadge.hidden = false;
        } else {
          headerBadge.hidden = true;
        }
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
